import React from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns-tz";
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Chip,
    Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { hasPermission } from "state/checkPermission";
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from "components/FlexBetween";
import FlexCenter from 'components/FlexCenter';


const PendingNotifList = ({ rows, onView }) => {
    const user = useSelector((state) => state.user);
    const theme = useTheme();

    if (!Array.isArray(rows) || rows.length === 0) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">Nothing to display</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {rows.map((member, index) => {
                const inactive = member.isActive === false;

                const canView = hasPermission(user?.permissions, "CrewMembers", "view", false);
                
                const sortedNotifications = [...member.notifications].sort(
                    (a, b) => new Date(a.logForDate) - new Date(b.logForDate)
                );
                
                const iconColor = (inactive)
                    ? theme.palette.text.disabled
                    : theme.palette.primary.dark;
                    
                return (
                    <Box
                        key={member._id || index}
                        sx={{
                            border: `1px solid ${inactive
                                ? theme.palette.neutral.light
                                : theme.palette.neutral.deep}`,
                            borderRadius: 2,
                            p: 1.5,
                            backgroundColor: inactive
                                ? theme.palette.action.hover
                                : "background.paper",
                            color: inactive
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                            opacity: inactive ? 0.75 : 1,
                        }}
                    >
                        <FlexBetween>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    label={index + 1}
                                    variant="outlined"
                                    color={"primary"}
                                    size="small"
                                    sx={{
                                        fontSize: "0.75rem",
                                        borderWidth: 1.5,
                                        height: 20,
                                        "& .MuiChip-label": {
                                            px: 1,
                                        },
                                    }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        color: inactive
                                            ? theme.palette.text.disabled
                                            : theme.palette.text.primary,
                                    }}
                                >
                                    {member.crewCode} - {member.crewName}
                                </Typography>
                            </Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: inactive
                                        ? theme.palette.text.disabled
                                        : theme.palette.text.secondary,
                                }}
                            >
                                {member.gender || "-"} | {member.post || "-"} | {member.base || "-"}
                            </Typography>
                        </FlexBetween>

                        <Divider sx={{ my: 0.5 }} />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {sortedNotifications?.map((notif, index) => (
                                <Box key={index} display="flex" flexDirection="column">
                                    <FlexBetween>
                                        <Typography variant="caption" width={20}>
                                            {index + 1}. 
                                        </Typography> 
                                        <Typography variant="caption" flexGrow={1}>
                                            {format(notif.logForDate, 'dd/MM/yy')} {notif.logMessage} by {notif.logByUser} at {notif.logOnDate} {notif.logOnTime}                  
                                        </Typography>
                                    </FlexBetween>                                                    
                                </Box>
                            ))}                          
                        </Box>

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            gap={2}
                            sx={{ mt: 1 }}
                        >                            
                            {canView && (
                                <Tooltip title="View">
                                    <IconButton
                                        size="small"
                                        disabled={inactive}
                                        onClick={() =>
                                            hasPermission(user?.permissions, "CrewPendingNotifications", "view") &&
                                            onView(member)
                                        }
                                    >
                                        <VisibilityIcon fontSize="small" sx={{ color: iconColor }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default PendingNotifList;
